import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardBody, Row, Col, Badge, Button, Spinner, CardHeader } from 'reactstrap';
// EditBuilderModal lives in pages/Builder
// import EditBuilderModal from '../Builder/EditBuilderModal';
import { useQuery } from '@tanstack/react-query';
import profile_placeholder from 'assets/images/profile_placeholder.png';
import { formatFullName, getTruncatedId } from 'lib/utils/formatters';
// Import social media and 2FA icons
import googleIcon from 'assets/images/icons/google.svg';
import facebookIcon from 'assets/images/icons/facebook.svg';
import appleIcon from 'assets/images/icons/apple.svg';
import smsIcon from 'assets/images/icons/sms-notification.svg';
import authenticatorIcon from 'assets/images/icons/authenticator.svg';
import { userDetailById } from 'endpoints/users/usersListingspage.action';
import UserEditModal from 'pages/Users/Modal/UserEditModal';

interface BasicInformationProps {
    id: string;
}

const BasicInformation: React.FC<BasicInformationProps> = ({ id }) => {
    const [isFirstMount, setIsFirstMount] = useState(true);
    const apiCallRef = useRef(false);

    // Helper: format createdAt to "22 Oct 2020, 12:30 PM"
    const formatDate = useCallback((dateStr?: string | null) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr; // return original if invalid
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = d.getDate();
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        return `${day} ${month} ${year}, ${time}`;
    }, []);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['user', id],
        queryFn: () => {
            apiCallRef.current = true;
            return userDetailById(id);
        },
        enabled: !!id && !isFirstMount,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        retry: 3,
    });

    // Handle first mount manually
    useEffect(() => {
        if (isFirstMount) {
            setIsFirstMount(false);
            // The query will now be enabled and run automatically
        }
    }, [isFirstMount]);

    const inspectorInfo = data?.data;
    // const [profileImage, setProfileImage] = useState<string>(builderinfo?.profile_image || '');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    // Function to copy ID to clipboard
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 1 second
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const handleEdit = (userId: string) => {
        setSelectedUser(userId);
        setEditModalOpen(true);
    };

    const handleRefetch = useCallback(() => {
        apiCallRef.current = false;
        refetch();
    }, [refetch]);

    // Reset image loading state when user data changes
    useEffect(() => {
        if (inspectorInfo?.profile_image) {
            setImageLoading(true);
            setImageError(false);
        }
    }, [inspectorInfo?.profile_image]);

    // Handle image load events
    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageLoading(false);
        setImageError(true);
    };

    return (
        <Card className="mb-3 " style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            <CardHeader className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Basic Information</h6>
                    <Button color="link" size="md" className="text-muted d-flex align-items-center p-0" onClick={() => handleEdit(id)}>
                        Edit <i className="ri-pencil-line ms-1 text-muted"></i>
                    </Button>
                </div>
            </CardHeader>
            <CardBody className="pb-2">

                {/* Loading State */}
                {isLoading && (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading User information...</p>
                    </div>
                )}

                {/* Content */}
                {!isLoading && (
                    <div className="d-flex align-items-center justify-content-start" style={{ gap: 40 }}>
                        <div className="d-flex flex-row align-items-center" style={{ gap: 20 }}>
                            <div className="rounded-circle bg-secondary bg-opacity-25" style={{ width: 70, height: 70, minWidth: 70, minHeight: 70, flex: '0 0 70px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                {/* Image Loader */}
                                {imageLoading && !imageError && (
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
                                        <Spinner size="sm" color="primary" />
                                    </div>
                                )}
                                <img 
                                    src={inspectorInfo?.profile_image === 'profile_skipped' || !inspectorInfo?.profile_image || imageError ? profile_placeholder : inspectorInfo?.profile_image} 
                                    alt="Profile" 
                                    className="rounded-circle" 
                                    style={{ 
                                        width: 70, 
                                        height: 70, 
                                        objectFit: 'cover', 
                                        display: 'block',
                                        opacity: imageLoading ? 0.3 : 1,
                                        transition: 'opacity 0.3s ease'
                                    }}
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                />
                            </div>
                            <div className="d-flex flex-row" style={{ gap: 20 }}>
                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'start' }}><span className="fw-semibold" style={{ fontSize: 16 }}>{formatFullName(inspectorInfo?.first_name, inspectorInfo?.last_name) || 'N/A'} </span>{inspectorInfo?.Account_status && <Badge color={inspectorInfo?.Account_status === 'active' ? 'success' : 'danger'} className="rounded-pill ms-2 px-2 py-1 text-light" style={{ fontWeight: 500, fontSize: 13, background: '#f3f4f6', border: 'none' }}>{inspectorInfo?.Account_status === 'active' ? 'Active' : 'Suspended'}</Badge>}</div>
                                    <span className="text-muted" style={{ fontSize: 15 }}>{inspectorInfo?.email || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="fw-semibold" style={{ fontSize: 16 }}>User ID</span>
                                    <div className="d-flex align-items-center gap-2">
                                        <span
                                            className="text-muted"
                                            style={{ fontSize: 15, fontFamily: 'monospace' }}
                                        >
                                            {getTruncatedId(inspectorInfo?.id)}
                                        </span>
                                        <span
                                            onClick={() => inspectorInfo?.id && copyToClipboard(inspectorInfo.id)}
                                            style={{
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: copiedId === inspectorInfo?.id ? '#28a745' : '#6b7280',
                                                transition: 'color 0.2s ease'
                                            }}
                                            title={copiedId === inspectorInfo?.id ? 'Copied!' : 'Copy full ID'}
                                        >
                                            {copiedId === inspectorInfo?.id ? (
                                                <i className="ri-check-line"></i>
                                            ) : (
                                                <i className="ri-file-copy-line"></i>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex flex-row align-items-center justify-content-center">
                            <Row className="align-items-center" style={{ gap: 30 }}>
                                <Col className="d-flex flex-column">
                                    <span className="fw-semibold" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>Phone Number</span>
                                    <span className="text-muted" style={{ fontSize: 15, whiteSpace: 'nowrap' }}>{inspectorInfo?.phone_no || 'N/A'}</span>
                                </Col>
                                {/* <Col className="d-flex flex-column">
                                    <span className="fw-semibold" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>Address</span>
                                    <span className="text-muted" style={{ fontSize: 15, whiteSpace: 'nowrap' }}>{inspectorInfo?.street_address || 'N/A'}</span>
                                </Col> */}
                                <Col className="d-flex flex-column">
                                    <span className="fw-semibold" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>Created At</span>
                                    <span className="text-muted" style={{ fontSize: 15, whiteSpace: 'nowrap' }}>{formatDate(inspectorInfo?.createdAt) || 'N/A'}</span>
                                </Col>
                                <Col className="d-flex flex-column">
                                    <span className="fw-semibold" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>Social Accounts</span>
                                    <div className="d-flex gap-2 align-items-center mt-1">
                                        <img
                                            src={googleIcon}
                                            alt="Google"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                opacity: inspectorInfo?.is_google_connected ? 1 : 0.5,
                                                filter: inspectorInfo?.is_google_connected ? 'none' : 'grayscale(100%)'
                                            }}
                                            title={`Google ${inspectorInfo?.is_google_connected ? 'Connected' : 'Not Connected'}`}
                                        />
                                        <img
                                            src={facebookIcon}
                                            alt="Facebook"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                opacity: inspectorInfo?.is_facebook_connected ? 1 : 0.5,
                                                filter: inspectorInfo?.is_facebook_connected ? 'none' : 'grayscale(100%)'
                                            }}
                                            title={`Facebook ${inspectorInfo?.is_facebook_connected ? 'Connected' : 'Not Connected'}`}
                                        />
                                        <img
                                            src={appleIcon}
                                            alt="Apple"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                opacity: inspectorInfo?.is_apple_connected ? 1 : 0.5,
                                                filter: inspectorInfo?.is_apple_connected ? 'none' : 'grayscale(100%)'
                                            }}
                                            title={`Apple ${inspectorInfo?.is_apple_connected ? 'Connected' : 'Not Connected'}`}
                                        />
                                    </div>
                                </Col>
                                <Col className="d-flex flex-column">
                                    <span className="fw-semibold" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>2FA Setup</span>
                                    <div className="d-flex gap-2 align-items-center mt-1">
                                        <img
                                            src={smsIcon}
                                            alt="SMS"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                opacity: inspectorInfo?.text_otp_2fa ? 1 : 0.5,
                                                filter: inspectorInfo?.text_otp_2fa ? 'none' : 'grayscale(100%)'
                                            }}
                                            title={`SMS 2FA ${inspectorInfo?.text_otp_2fa ? 'Enabled' : 'Disabled'}`}
                                        />
                                        <img
                                            src={authenticatorIcon}
                                            alt="Authenticator"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                opacity: inspectorInfo?.auth_app_2fa ? 1 : 0.5,
                                                filter: inspectorInfo?.auth_app_2fa ? 'none' : 'grayscale(100%)'
                                            }}
                                            title={`Auth App 2FA ${inspectorInfo?.auth_app_2fa ? 'Enabled' : 'Disabled'}`}
                                        />
                                    </div>
                                </Col>
                                {/* <Col className="d-flex flex-column mt-2">
                                <span className="fw-semibold" style={{ fontSize: 16, whiteSpace: 'nowrap' }}>Status</span>
                                <div className="d-flex align-items-center">
                                    <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)} direction="down">
                                        <DropdownToggle
                                            caret
                                            color="light"
                                            className="w-100 text-start px-2 py-1"
                                            style={{
                                                fontWeight: 500,
                                                fontSize: 15,
                                                borderRadius: 6
                                            }}
                                        >
                                            {status}
                                        </DropdownToggle>
                                        <DropdownMenu end style={{ minWidth: 130 }}>
                                            <DropdownItem onClick={() => setStatus('Active')}>Active</DropdownItem>
                                            <DropdownItem onClick={() => setStatus('Suspended')}>Suspended</DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </Col> */}
                            </Row>
                        </div>
                    </div>
                )}
            </CardBody>
            <UserEditModal
                isOpen={editModalOpen}
                toggle={() => { setEditModalOpen(false); setSelectedUser(null); }}
                user={selectedUser ?? ''}
                refetch={handleRefetch}
            />
        </Card>
    );
};

export default BasicInformation;